/**
 * Phase 8B — Create / edit a sub-category within a section.
 *   Revenue section: label only (abstract row)
 *   Expense section: label + multi-select Cost Center picker (≥1 required)
 *
 * CCs are fetched lazily when the modal opens for an expense section.
 * Selected CCs stay visible as chips regardless of search text.
 */

import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { fetchGet, toQueryString } from "../../../service/api_client";
import { MisSection, MisSubCategory } from "../models/mis_model";

interface CcOption {
  id: string;
  name: string;
  code?: string;
}

interface Props {
  open: boolean;
  section: MisSection;
  initial?: MisSubCategory | null;
  onClose: () => void;
  onSubmit: (payload: {
    label: string;
    cost_center_ids: string[];
  }) => Promise<void>;
}

const SubCategoryModal: FunctionComponent<Props> = ({
  open,
  section,
  initial,
  onClose,
  onSubmit,
}) => {
  const isEdit = !!initial;
  const needsCC = section.section_type === "expense";
  const ccUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/cost_center`;

  const [label, setLabel] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Cache CC objects we've seen so selected chips can render label + code
  // even after the user changes the search term.
  const [ccCache, setCcCache] = useState<Record<string, CcOption>>({});
  const [ccSearch, setCcSearch] = useState("");
  const [ccs, setCcs] = useState<CcOption[]>([]);
  const [ccLoading, setCcLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setLabel(initial?.label ?? "");
    setSelectedIds(initial?.cost_center_ids ?? []);
    setCcSearch("");
    setError(null);
    setSaving(false);
  }, [open, initial]);

  // Fetch CCs for expense sections. Also fetch details for any pre-selected
  // ids that aren't in the current result set (so their chips render properly).
  useEffect(() => {
    if (!open || !needsCC) return;
    const q = ccSearch.trim();
    setCcLoading(true);
    fetchGet(
      ccUrl + toQueryString({ page: 1, count: 30, ...(q ? { search: q } : {}) }),
    )
      .then((res: any) => {
        if (res?.success) {
          const rows: any[] = res.data?.data || [];
          const mapped: CcOption[] = rows.map((r) => ({
            id: r.id,
            name: r.name || r.title || r.id,
            code: r.code,
          }));
          setCcs(mapped);
          setCcCache((prev) => {
            const next = { ...prev };
            for (const c of mapped) next[c.id] = c;
            return next;
          });
        }
      })
      .finally(() => setCcLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ccSearch, needsCC]);

  // Prime cache with pre-selected ids on first open (fetches each by id).
  useEffect(() => {
    if (!open || !needsCC || !initial) return;
    const missing = (initial.cost_center_ids || []).filter(
      (id) => !ccCache[id],
    );
    if (missing.length === 0) return;
    Promise.all(
      missing.map((id) =>
        fetchGet(`${ccUrl}/${id}`).then((r: any) =>
          r?.success && r.data
            ? {
                id: r.data.id,
                name: r.data.name || r.data.title || r.data.id,
                code: r.data.code,
              }
            : null,
        ),
      ),
    ).then((results) => {
      const next: Record<string, CcOption> = {};
      for (const c of results) if (c) next[c.id] = c;
      setCcCache((prev) => ({ ...prev, ...next }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsCC, initial]);

  const selectedChips = useMemo<CcOption[]>(
    () =>
      selectedIds.map(
        (id) => ccCache[id] || { id, name: id, code: undefined },
      ),
    [selectedIds, ccCache],
  );

  if (!open) return null;

  const toggle = (cc: CcOption) => {
    setSelectedIds((prev) => {
      if (prev.includes(cc.id)) return prev.filter((x) => x !== cc.id);
      // Auto-fill label with first CC's name if label is still empty.
      if (!label.trim() && prev.length === 0) setLabel(cc.name);
      return [...prev, cc.id];
    });
  };

  const removeChip = (id: string) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const handleSubmit = async () => {
    setError(null);
    if (!label.trim()) {
      setError("Label is required.");
      return;
    }
    if (needsCC && selectedIds.length === 0) {
      setError("Please pick at least one Cost Center.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        label: label.trim(),
        cost_center_ids: needsCC ? selectedIds : [],
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">
            {isEdit ? "Edit sub-category" : "Add sub-category"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Label<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={
                needsCC
                  ? "e.g. Production (auto-fills from first CC)"
                  : "e.g. Charge-points"
              }
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-myPrimaryColor"
            />
          </div>

          {needsCC && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Cost Centers<span className="text-red-500">*</span>
                {selectedIds.length > 0 && (
                  <span className="ml-2 text-gray-500 font-normal">
                    ({selectedIds.length} selected)
                  </span>
                )}
              </label>

              {selectedChips.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {selectedChips.map((cc) => (
                    <span
                      key={cc.id}
                      className="inline-flex items-center gap-1 bg-myPrimaryColor bg-opacity-10 border border-myPrimaryColor text-myPrimaryColor rounded px-2 py-0.5 text-xs"
                    >
                      {cc.name}
                      <button
                        onClick={() => removeChip(cc.id)}
                        className="hover:text-red-600"
                        title="Remove"
                      >
                        <Icon path={mdiClose} className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <input
                ref={searchRef}
                type="text"
                value={ccSearch}
                onChange={(e) => setCcSearch(e.target.value)}
                placeholder="Search CCs by name…"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-myPrimaryColor"
              />
              <div className="max-h-52 overflow-auto border border-gray-200 rounded">
                {ccLoading && (
                  <div className="p-3 text-xs text-gray-500">Loading…</div>
                )}
                {!ccLoading && ccs.length === 0 && (
                  <div className="p-3 text-xs text-gray-500">
                    No matching cost centers.
                  </div>
                )}
                {!ccLoading &&
                  ccs.map((cc) => {
                    const isSelected = selectedIds.includes(cc.id);
                    return (
                      <label
                        key={cc.id}
                        className={`flex items-center gap-2 px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer ${
                          isSelected
                            ? "bg-myPrimaryColor bg-opacity-5"
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(cc)}
                          className="rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-800 truncate">
                            {cc.name}
                          </div>
                          {cc.code && (
                            <div className="text-xs text-gray-500 truncate">
                              {cc.code}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
              </div>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-myPrimaryColor hover:bg-mySecondaryColor rounded disabled:opacity-50"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add sub-category"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubCategoryModal;
